import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useChat } from '../hooks/useChat';
import { getUserId } from '../utils/auth';
import { useAuth } from '../auth/AuthContext';
import { BASE_URL } from '../config';

import {
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  Avatar,
  IconButton,
  Alert
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from '../admin/hooks/useTranslation';

type MatchedUser = {
  matchedUserId: string;
  firstName: string;
  profilePhotoId: string;
};

const ChatPage = () => {
  const { t } = useTranslation();
  const { userId: targetUserId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { state } = useLocation();
  const myUserId = getUserId();
  const { token, refresh } = useAuth();

  const [matchedUser, setMatchedUser] = useState<MatchedUser | null>(
    (state as { matchedUser?: MatchedUser })?.matchedUser || null
  );
  const [loadingUser, setLoadingUser] = useState(!matchedUser);
  const [error, setError] = useState('');
  const {
    messages,
    sendMessage,
    connected,
    markAsRead,
    loadOlderMessages,
    hasMore,
    loading,
    sendTypingStatus,
    typingStatus
  } = useChat(targetUserId || '');
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollBoxRef = useRef<HTMLDivElement>(null);
  const prevScrollHeightRef = useRef<number>(0);
  const lastScrollTop = useRef<number>(0);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const handleResize = () => {
        const visualViewport = window.visualViewport;
        if (visualViewport) {
          const viewportHeight = visualViewport.height;
          const windowHeight = window.innerHeight;
          const keyboardHeight = windowHeight - viewportHeight;
          setKeyboardHeight(keyboardHeight > 50 ? keyboardHeight : 0);
        }
      };

      window.visualViewport?.addEventListener('resize', handleResize);
      return () => window.visualViewport?.removeEventListener('resize', handleResize);
    }
  }, []);

  useEffect(() => {
    if (!matchedUser && targetUserId) {
      const fetchMatchedUsers = async () => {
        try {
          const res = await axios.get(`${BASE_URL}/api/user/matched-users`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (res.data.status === 'ok') {
            const found = res.data.data.find(
              (user: MatchedUser) => user.matchedUserId === targetUserId
            );
            if (found) {
              setMatchedUser(found);
            } else {
              setError('Kullanıcı bulunamadı.');
            }
          } else {
            setError('Kullanıcı bilgisi getirilemedi.');
          }
        } catch (err: any) {
          if (err.response?.status === 401) {
            const refreshed = await refresh?.();
            if (refreshed) {
              fetchMatchedUsers();
            } else {
              setError('Oturum süresi doldu.');
            }
          } else {
            setError('Sunucu hatası.');
          }
        } finally {
          setLoadingUser(false);
        }
      };

      fetchMatchedUsers();
    }
  }, [matchedUser, targetUserId, token, refresh]);

  useEffect(() => {
    if (targetUserId) {
      markAsRead();
    }
  }, [targetUserId]);


  useEffect(() => {
    if (!loadingUser && inputRef.current) {
      inputRef.current.focus();
    }
  }, [loadingUser]);

  const isFetchingOlderRef = useRef(false);
  const userScrolledUpRef = useRef(false); // Kullanıcının yukarı scroll yapıp yapmadığını tutacak

  // Scroll event handler içindeki kısma:
  // Scroll eventinde kullanıcı yukarı kaydırıyorsa userScrolledUpRef.current = true; değilse false yapalım
  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const currentScrollTop = target.scrollTop;
    const maxScrollTop = target.scrollHeight - target.clientHeight;

    const isScrollingUp = currentScrollTop < lastScrollTop.current;
    if (isScrollingUp) {
      userScrolledUpRef.current = true; // kullanıcı yukarı kaydırıyor
    } else if (currentScrollTop >= maxScrollTop - 20) {
      // en alta yakınsa yukarı scroll değilsin demektir
      userScrolledUpRef.current = false;
    }

    if (isScrollingUp && currentScrollTop < 100 && hasMore && !loading) {
      loadOlderMessages();
    }

    lastScrollTop.current = currentScrollTop;
  };

  useEffect(() => {
    if (loading) {
      if (scrollBoxRef.current) {
        isFetchingOlderRef.current = true;
        prevScrollHeightRef.current = scrollBoxRef.current.scrollHeight;
      }
    } else {
      if (scrollBoxRef.current && isFetchingOlderRef.current) {
        // Eski mesajlar yüklendi, scrollTop'u eski pozisyona ayarla
        const currentHeight = scrollBoxRef.current.scrollHeight;
        const scrollTop = scrollBoxRef.current.scrollTop;
        const heightDiff = currentHeight - prevScrollHeightRef.current;
        scrollBoxRef.current.scrollTop = scrollTop + heightDiff;

        isFetchingOlderRef.current = false;
      } else if (scrollBoxRef.current && !isFetchingOlderRef.current) {
        // Yeni mesaj geldiğinde, eğer kullanıcı en altta veya çok yakınsa otomatik scroll yap
        const maxScrollTop = scrollBoxRef.current.scrollHeight - scrollBoxRef.current.clientHeight;
        const scrollTop = scrollBoxRef.current.scrollTop;

        // Eğer kullanıcı yukarı kaydırmışsa (userScrolledUpRef true) scroll aşağı kaydırma
        if (!userScrolledUpRef.current || scrollTop > maxScrollTop - 100) {
          scrollBoxRef.current.scrollTop = scrollBoxRef.current.scrollHeight;
          userScrolledUpRef.current = false; // scroll aşağıya kaydırıldı, scroll yukarı değil artık
        }
      }
    }
  }, [loading, messages]);

  useEffect(() => {
    if (scrollBoxRef.current) {
      scrollBoxRef.current.scrollTop = scrollBoxRef.current.scrollHeight;
    }
  }, [messages]);


  const handleSend = () => {
    if (input.trim()) {
      sendMessage(input);
      setInput('');
      inputRef.current?.focus();
    }
  };

  useEffect(() => {
    if (!loadingUser && messages.length > 0) {
      setTimeout(() => {
        if (scrollBoxRef.current) {
          scrollBoxRef.current.scrollTop = scrollBoxRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [loadingUser]);

  if (loadingUser) {
    return <Box p={2}><Typography>Loading...</Typography></Box>;
  }

  if (!matchedUser || error) {
    return (
      <Box p={2}>
        <Alert severity="error">
          {error || 'Kullanıcı bilgisi eksik.'}{' '}
          <Button onClick={() => navigate('/messages')}>Geri Dön</Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{
      position: 'fixed',
      top: 'env(safe-area-inset-top)',
      left: 0,
      right: 0,
      bottom: `calc(env(safe-area-inset-bottom) + ${keyboardHeight}px)`,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#f5f5f5',
      overflow: 'hidden',
    }}>
      {/* Üst Bar */}
      <Box
        sx={{
        flexShrink: 0,
        py: 1,
        backgroundColor: 'background.paper',
        borderBottom: '1px solid #ddd',
        height: 55,
        zIndex: 10,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton onClick={() => navigate('/messages')}>
            <ArrowBackIcon />
          </IconButton>
          <Avatar
            src={
              matchedUser.profilePhotoId
                ? `${BASE_URL}/object-store/img/get/${matchedUser.matchedUserId}/SMALL/${matchedUser.profilePhotoId}`
                : '/placeholder.png'
            }
            alt={matchedUser.firstName}
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: typingStatus ? 'flex-start' : 'center',
              overflow: 'hidden',       // Taşmayı engelle
              height: 40,               // Sabit yükseklik
              lineHeight: 1,            // Satır yüksekliği
            }}
          >
            <Typography
              variant="h6"
              component="div"           // block yap
              sx={{
                transition: 'all 0.3s ease',
                transform: typingStatus ? 'translateY(0)' : 'translateY(6px)',
                fontSize: typingStatus ? '1rem' : '1.25rem',
                lineHeight: 1,
              }}
            >
              {matchedUser.firstName}
            </Typography>
            <Box
              sx={{
                height: 22,
                transition: 'all 0.3s ease',
                opacity: typingStatus ? 1 : 0,
                transform: typingStatus ? 'translateY(0)' : 'translateY(-5px)',
                overflow: 'hidden',    // Burada da taşmayı engelle
              }}
            >
              <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1 }}>
                Typing...
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Box>

      {/* Mesaj Alanı */}
      <Box
        ref={scrollBoxRef}
        sx={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        px: 1,
        py: 1,
        WebkitOverflowScrolling: 'touch',
        height: `calc(100% - ${55 + 56}px)`, // Üst bar (55px) + Input alanı (~56px)
        }}
        onScroll={onScroll}
      >
        <Stack spacing={1}>
          {[...messages]
            .sort((a, b) => new Date(a.sendDateTime).getTime() - new Date(b.sendDateTime).getTime())
            .map((msg) => {
              const isOwn = String(msg.senderUser) === String(myUserId);
              const align = isOwn ? 'flex-end' : 'flex-start';
              const bg = isOwn ? 'primary.light' : 'grey.300';

              return (
                <Box key={msg.id} display="flex" justifyContent={align}>
                  <Box
                    sx={{
                      backgroundColor: bg,
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      maxWidth: '70%',
                      wordBreak: 'break-word',
                    }}
                  >
                    <Typography variant="body2">{msg.content}</Typography>
                    <Typography variant="caption" display="block" textAlign="right">
                      {(() => {
                        const date = new Date(msg.sendDateTime);
                        const now = new Date();
                        const isToday =
                          date.getDate() === now.getDate() &&
                          date.getMonth() === now.getMonth() &&
                          date.getFullYear() === now.getFullYear();

                        return isToday
                          ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                          : date.toLocaleString([], {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          });
                      })()}
                      {isOwn && msg.read && ' ✓✓'}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          <div ref={scrollRef} />
        </Stack>
      </Box>
      {/* Alt Yazıyor Alanı */}
      {typingStatus && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 65, // Daha aşağıda
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#FFFFFF',
            borderRadius: 10,
            px: 1.5,
            py: 0.5,
            display: 'flex',
            alignItems: 'center',
            boxShadow: 2,
            zIndex: 5,
            pointerEvents: 'none',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
            }}
          >
            {[0, 1, 2].map((i) => (
              <Box
                key={i}
                component="span"
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  backgroundColor: '#111',
                  animation: 'typingBounce 1.2s infinite',
                  animationDelay: `${i * 0.2}s`,
                }}
              />
            ))}
          </Box>
          <style>
            {`
        @keyframes typingBounce {
          0%, 80%, 100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-4px);
          }
        }
      `}
          </style>
        </Box>
      )}

      {/* Giriş Alanı */}
      {/* Mesaj Yazma Alanı */}
      <Box
        sx={{
        flexShrink: 0,
        p: 1,
        backgroundColor: 'background.paper',
        borderTop: '1px solid #ccc',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Mesaj yaz..."
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              sendTypingStatus(); // typing indicator
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            inputRef={inputRef}
            size="small"
            autoComplete="off"
          />
          <IconButton color="primary" onClick={handleSend}>
            <SendIcon />
          </IconButton>
        </Stack>
      </Box>
    </Box>
  );
};

export default ChatPage;